import React from 'react';
import '../styles/EmaPanel.css';

// ── Sparkline SVG ─────────────────────────────────────────────────────────────
const Sparkline = ({ bars }) => {
  if (!bars || bars.length < 2) return null;

  const W = 260, H = 56, PAD = 4;
  const closes = bars.map(b => b.close);
  const emas   = bars.map(b => b.ema).filter(v => v != null);
  const ema5s  = bars.map(b => b.ema5).filter(v => v != null);

  const allVals = [...closes, ...emas, ...ema5s].filter(Boolean);
  const min = Math.min(...allVals) * 0.9998;
  const max = Math.max(...allVals) * 1.0002;
  const range = max - min || 1;

  const toX = i => PAD + (i / (bars.length - 1)) * (W - PAD * 2);
  const toY = v => H - PAD - ((v - min) / range) * (H - PAD * 2);

  // Price line points
  const pricePts = bars.map((b, i) => `${toX(i)},${toY(b.close)}`).join(' ');

  // EMA line points (only where ema is set)
  const emaBarPts = bars
    .map((b, i) => b.ema != null ? `${toX(i)},${toY(b.ema)}` : null)
    .filter(Boolean)
    .join(' ');

  // EMA 5 line points (only where ema5 is set)
  const ema5BarPts = bars
    .map((b, i) => b.ema5 != null ? `${toX(i)},${toY(b.ema5)}` : null)
    .filter(Boolean)
    .join(' ');

  // Last bar: above or below EMA?
  const last = bars[bars.length - 1];
  const priceColor = last.ema == null || last.close >= last.ema ? '#00c853' : '#e53935';

  // Shade area between price and EMA
  const lastEmaIdx = bars.reduce((acc, b, i) => b.ema != null ? i : acc, -1);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className="ema-sparkline">
      {/* Price line */}
      <polyline
        points={pricePts}
        fill="none"
        stroke={priceColor}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* EMA line */}
      {emaBarPts && (
        <polyline
          points={emaBarPts}
          fill="none"
          stroke="#ff9800"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeDasharray="3 2"
        />
      )}
      {/* EMA 5 line */}
      {ema5BarPts && (
        <polyline
          points={ema5BarPts}
          fill="none"
          stroke="#42a5f5"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      )}
      {/* Current price dot */}
      <circle
        cx={toX(bars.length - 1)}
        cy={toY(last.close)}
        r="3"
        fill={priceColor}
      />
      {/* EMA 5 dot */}
      {last.ema5 != null && (
        <circle
          cx={toX(bars.length - 1)}
          cy={toY(last.ema5)}
          r="2.5"
          fill="#42a5f5"
        />
      )}
      {/* EMA dot */}
      {last.ema != null && (
        <circle
          cx={toX(bars.length - 1)}
          cy={toY(last.ema)}
          r="2.5"
          fill="#ff9800"
        />
      )}
    </svg>
  );
};

// ── VWAP condition checklist helper ───────────────────────────────────────────
const VwapChecklist = ({ title, score, conditions, bull = false }) => (
  <div className={`vwap-cl ${bull ? 'vwap-cl-bull' : 'vwap-cl-bear'}`}>
    <div className="vwap-cl-title">
      {title}
      <span className={`vwap-cl-score ${score >= 3 ? (bull ? 'vwap-cl-score-bull' : 'vwap-cl-score-bear') : ''}`}>
        {score}/4
      </span>
    </div>
    {conditions.map((c, i) => (
      <div key={i} className={`vwap-cl-cond ${c.met ? (bull ? 'vwap-cond-met-bull' : 'vwap-cond-met-bear') : 'vwap-cond-unmet'}`}>
        <span>{c.met ? '✓' : '✗'}</span> {c.label}
      </div>
    ))}
  </div>
);

// ── Trend badge helper ─────────────────────────────────────────────────────────
const TrendBadge = ({ trend }) => {
  if (!trend || trend === 'NOT_READY') return null;
  const cls = trend === 'BULLISH' ? 'ema-mtf-bull' : trend === 'BEARISH' ? 'ema-mtf-bear' : 'ema-mtf-sideways';
  const icon = trend === 'BULLISH' ? '↑' : trend === 'BEARISH' ? '↓' : '→';
  return <span className={`ema-mtf-badge ${cls}`}>{icon} {trend}</span>;
};

// ── Main Component ────────────────────────────────────────────────────────────
const EmaPanel = ({ emaData }) => {
  if (!emaData) {
    return (
      <div className="ema-panel">
        <div className="ema-header">
          <span className="ema-title">20 EMA · 5-min</span>
          <span className="ema-signal ema-signal-loading">Waiting</span>
        </div>
        <div className="ema-loading">
          Start analysis to begin collecting 5-min price bars.
        </div>
      </div>
    );
  }

  const {
    currentPrice,
    ema20,
    ema5,
    emaTrend,
    emaTrendSlope5,
    emaTrendSlope20,
    priceAboveEma,
    emaSlope,
    signal,
    crossover,
    regime,
    barsComputed,
    hasEnoughData,
    recentBars = [],
    ema65,
    priceAboveEma65,
    higherTfRegime,
    ema65Slope,
    barsComputed15m = 0,
    barsComputed1h = 0,
    ema1h,
    dailyBias,
    dayOpenPrice,
    trend15m,
    trend1h,
    trendDaily,
    overallTrend,
    tfBias,
    tfAlignedCount = 0,
    vwap,
    vwapSource,
    priceAboveVwap,
    vwapSlope,
    vwapSlopingUp,
    vwapSlopingDown,
    pullbackHeldAboveVwap,
    pullbackFailedAtVwap,
    callVwapScore = 0,
    putVwapScore  = 0,
    vwapSetupSignal,
    todayBarCount = 0,
  } = emaData;

  const fmt  = v  => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A';
  const fmtP = v  => v != null ? (v > 0 ? '+' : '') + v.toFixed(3) + '%' : 'N/A';

  const priceDiff = (currentPrice && ema20) ? ((currentPrice - ema20) / ema20) * 100 : null;

  const regimeColor =
    regime === 'STRONG_BULL' ? '#00c853' :
    regime === 'BULL'        ? '#69f0ae' :
    regime === 'STRONG_BEAR' ? '#d32f2f' :
    regime === 'BEAR'        ? '#ef9a9a' :
    '#9e9e9e';

  const signalColor = signal === 'BULLISH' ? '#00c853' : '#d32f2f';

  const slopeArrow = emaSlope > 0.02 ? '↗' : emaSlope < -0.02 ? '↘' : '→';

  return (
    <div className="ema-panel">
      <div className="ema-header">
        <span className="ema-title">EMA · Multi-Timeframe</span>
        {hasEnoughData ? (
          <span className="ema-signal" style={{ background: signalColor }}>{signal}</span>
        ) : (
          <span className="ema-signal ema-signal-loading">
            {barsComputed}/{20} bars
          </span>
        )}
      </div>

      {!hasEnoughData ? (
        <div className="ema-loading">
          <div className="ema-progress-label">
            Collecting 5-min bars: {barsComputed || 0} / {20}
          </div>
          <div className="ema-progress-track">
            <div
              className="ema-progress-fill"
              style={{ width: `${Math.round(((barsComputed || 0) / 20) * 100)}%` }}
            />
          </div>
          <div className="ema-progress-hint">
            Each bar = one poll cycle. EMA computes at {20} bars.
          </div>
        </div>
      ) : (
        <>
          {/* ── Crossover alert ──────────────────────────────────────── */}
          {crossover !== 'NONE' && (
            <div className={`ema-crossover ${crossover === 'GOLDEN_CROSS' ? 'ema-cross-bull' : 'ema-cross-bear'}`}>
              {crossover === 'GOLDEN_CROSS'
                ? '▲ GOLDEN CROSS — Price crossed above EMA'
                : '▼ DEATH CROSS — Price crossed below EMA'}
            </div>
          )}

          {/* ── Price vs EMA row ─────────────────────────────────────── */}
          <div className="ema-values">
            <div className="ema-value-item">
              <span className="ema-value-label">Price</span>
              <span className="ema-value-num" style={{ color: signalColor }}>{fmt(currentPrice)}</span>
            </div>
            <div className="ema-value-item">
              <span className="ema-value-label">EMA 5</span>
              <span className="ema-value-num" style={{ color: '#42a5f5' }}>{fmt(ema5)}</span>
            </div>
            <div className="ema-value-item">
              <span className="ema-value-label">EMA 20</span>
              <span className="ema-value-num" style={{ color: '#ff9800' }}>{fmt(ema20)}</span>
            </div>
            <div className="ema-value-item">
              <span className="ema-value-label">Δ vs EMA</span>
              <span className="ema-value-num" style={{ color: priceDiff >= 0 ? '#00c853' : '#e53935' }}>
                {fmtP(priceDiff)}
              </span>
            </div>
            <div className="ema-value-item">
              <span className="ema-value-label">EMA Slope</span>
              <span className="ema-value-num" style={{ color: emaSlope > 0 ? '#00c853' : emaSlope < 0 ? '#e53935' : '#888' }}>
                {slopeArrow} {fmtP(emaSlope)}
              </span>
            </div>
          </div>

          {/* ── Regime pill ──────────────────────────────────────────── */}
          <div className="ema-regime-row">
            <span className="ema-regime" style={{ background: regimeColor }}>
              {regime?.replace('_', ' ')}
            </span>
            <span className="ema-bars-count">{barsComputed} bars · 5-min TF</span>
          </div>

          {/* ── 5/20 EMA trend trigger — a fresh CALL/PUT setup opens a SYSTEM paper trade (EMA TREND) ── */}
          {emaTrend && (
            <div className="ema-regime-row" title="Both EMAs sloping steeply the same way, bar closed beyond both. Slopes in ATR per 5-min bar; a trigger needs ≥0.31 (EMA5) and ≥0.22 (EMA20). The first bar of a setup opens a SYSTEM trade (verdict EMA TREND) under the daily limits and portfolio caps.">
              <span className="ema-regime" style={{
                background: emaTrend === 'CALL' ? '#00c853' : emaTrend === 'PUT' ? '#e53935' : '#555',
              }}>
                5/20 EMA: {emaTrend === 'CALL' ? 'CALL setup' : emaTrend === 'PUT' ? 'PUT setup'
                  : emaTrend === 'BETWEEN' ? 'between EMAs — no trade' : 'no steep trend'}
              </span>
              <span className="ema-bars-count">
                slope EMA5 {emaTrendSlope5 != null ? emaTrendSlope5.toFixed(2) : '—'} · EMA20 {emaTrendSlope20 != null ? emaTrendSlope20.toFixed(2) : '—'}
              </span>
            </div>
          )}

          {/* ── Sparkline ────────────────────────────────────────────── */}
          <div className="ema-chart">
            <Sparkline bars={recentBars} />
            <div className="ema-chart-legend">
              <span><span className="ema-legend-line" style={{ background: '#00c853' }} /> Price</span>
              <span><span className="ema-legend-line" style={{ background: '#42a5f5' }} /> EMA 5</span>
              <span><span className="ema-legend-line ema-legend-dashed" style={{ background: '#ff9800' }} /> EMA 20</span>
            </div>
          </div>

          {/* ── Multi-timeframe trend table ───────────────────────────────── */}
          <div className="ema-mtf-section">
            <div className="ema-mtf-title">Market Trend · Multi-Timeframe</div>

            {/* 15-min entry timing */}
            {trend15m && trend15m !== 'NOT_READY' ? (
              <div className={`ema-mtf-row ${trend15m === 'SIDEWAYS' ? 'ema-mtf-row-conflict' : ''}`}>
                <span className="ema-mtf-tf">15-min</span>
                <span className="ema-mtf-tf-sub">Entry timing</span>
                <TrendBadge trend={trend15m} />
                <span className="ema-mtf-val">{ema65 != null ? ema65.toFixed(0) : '—'}</span>
              </div>
            ) : (
              <div className="ema-mtf-row ema-mtf-row-loading">
                <span className="ema-mtf-tf">15-min</span>
                <span className="ema-mtf-loading-text">
                  collecting ({barsComputed15m}/9 bars · ~{Math.max(0, (9 - barsComputed15m) * 15)} min)
                </span>
              </div>
            )}

            {/* 1-hour swing direction */}
            {trend1h && trend1h !== 'NOT_READY' ? (
              <div className={`ema-mtf-row ${trend1h === 'SIDEWAYS' ? 'ema-mtf-row-conflict' : ''}`}>
                <span className="ema-mtf-tf">1-hour</span>
                <span className="ema-mtf-tf-sub">Swing direction</span>
                <TrendBadge trend={trend1h} />
                <span className="ema-mtf-val">{ema1h != null ? ema1h.toFixed(0) : '—'}</span>
              </div>
            ) : (
              <div className="ema-mtf-row ema-mtf-row-loading">
                <span className="ema-mtf-tf">1-hour</span>
                <span className="ema-mtf-loading-text">
                  collecting ({barsComputed1h}/3 bars · ~{Math.max(0, (3 - barsComputed1h) * 60)} min)
                </span>
              </div>
            )}

            {/* Daily overall trend */}
            {trendDaily && trendDaily !== 'NOT_READY' ? (
              <div className={`ema-mtf-row ${trendDaily === 'SIDEWAYS' ? 'ema-mtf-row-conflict' : ''}`}>
                <span className="ema-mtf-tf">Daily</span>
                <span className="ema-mtf-tf-sub">Overall trend</span>
                <TrendBadge trend={trendDaily} />
                <span className="ema-mtf-val">
                  Open {dayOpenPrice != null ? dayOpenPrice.toFixed(0) : '—'}
                </span>
              </div>
            ) : (
              <div className="ema-mtf-row ema-mtf-row-loading">
                <span className="ema-mtf-tf">Daily</span>
                <span className="ema-mtf-loading-text">waiting for day-open tick</span>
              </div>
            )}

            {/* TF Bias banner */}
            {tfBias && tfBias !== 'NOT_READY' && (
              <div className={`ema-tf-bias ema-tf-bias-${tfBias === 'BUY_CE' ? 'bull' : tfBias === 'BUY_PE' ? 'bear' : 'avoid'}`}>
                {tfBias === 'BUY_CE' && `BUY CE — ${tfAlignedCount}/3 TFs bullish`}
                {tfBias === 'BUY_PE' && `BUY PE — ${tfAlignedCount}/3 TFs bearish`}
                {tfBias === 'AVOID'  && 'AVOID — Sideways market, theta works against you'}
              </div>
            )}
          </div>

          {/* ── Strategy note ─────────────────────────────────────────── */}
          <div className={`ema-strategy-note ${
            tfBias === 'BUY_CE' ? 'ema-note-bull' :
            tfBias === 'BUY_PE' ? 'ema-note-bear' :
            tfBias === 'AVOID'  ? 'ema-note-avoid' : ''
          }`}>
            {tfBias === 'BUY_CE'
              ? `Uptrend confirmed. Buy CE on dips to EMA. Overall: ${overallTrend?.replace(/_/g, ' ')}.`
              : tfBias === 'BUY_PE'
              ? `Downtrend confirmed. Buy PE on rallies to EMA. Overall: ${overallTrend?.replace(/_/g, ' ')}.`
              : tfBias === 'AVOID'
              ? 'Sideways market — theta decay will hurt option buyers. Wait for trend to establish.'
              : priceAboveEma
              ? 'Price above EMA — bias BULLISH. Look for CALL setups on pullbacks to EMA.'
              : 'Price below EMA — bias BEARISH. Look for PUT setups on rallies to EMA.'}
          </div>

          {/* ── VWAP section ──────────────────────────────────────────── */}
          {vwap != null ? (
            <div className="vwap-section">
              <div className="vwap-header">
                <span className="vwap-title">VWAP · Step 6</span>
                {vwapSetupSignal && vwapSetupSignal !== 'NEUTRAL' && (
                  <span className={`vwap-signal-badge vwap-signal-${
                    vwapSetupSignal === 'CALL_SETUP' ? 'bull' :
                    vwapSetupSignal === 'PUT_SETUP'  ? 'bear' : 'watch'}`}>
                    {vwapSetupSignal === 'CALL_SETUP' ? 'CALL SETUP ↑'
                     : vwapSetupSignal === 'PUT_SETUP' ? 'PUT SETUP ↓'
                     : 'WATCH'}
                  </span>
                )}
              </div>

              {/* Price vs VWAP row */}
              <div className="vwap-row">
                <div className="vwap-kv">
                  <span className="vwap-kv-label"
                        title={vwapSource === 'FUTURES'
                          ? "Near-month future's day VWAP (volume-weighted), moved onto the index by the futures–index gap"
                          : "Average of today's 5-min closes — no fresh futures tick (index spot has no volume)"}>
                    VWAP {vwapSource === 'FUTURES' ? '· fut' : '· avg'}
                  </span>
                  <span className="vwap-kv-val" style={{ color: '#ff9800' }}>
                    {vwap.toFixed(0)}
                  </span>
                </div>
                <div className="vwap-kv">
                  <span className="vwap-kv-label">Position</span>
                  <span className={`vwap-pos-badge ${priceAboveVwap ? 'vwap-above' : 'vwap-below'}`}>
                    {priceAboveVwap ? '↑ Above' : '↓ Below'}
                  </span>
                </div>
                <div className="vwap-kv">
                  <span className="vwap-kv-label">Slope</span>
                  <span className="vwap-kv-val" style={{
                    color: vwapSlopingUp ? '#00c853' : vwapSlopingDown ? '#d32f2f' : '#888'
                  }}>
                    {vwapSlopingUp ? '↗' : vwapSlopingDown ? '↘' : '→'}
                    {' '}{vwapSlope != null ? (vwapSlope > 0 ? '+' : '') + vwapSlope.toFixed(3) + '%' : ''}
                  </span>
                </div>
                <div className="vwap-kv">
                  <span className="vwap-kv-label">Bars today</span>
                  <span className="vwap-kv-val">{todayBarCount}</span>
                </div>
              </div>

              {/* Condition checklists — show whichever side is stronger */}
              <div className="vwap-checklists">
                <VwapChecklist
                  title="Call Setup"
                  score={callVwapScore}
                  bull
                  conditions={[
                    { label: 'Price above VWAP',     met: priceAboveVwap },
                    { label: 'VWAP sloping up',       met: vwapSlopingUp },
                    { label: 'Pullback held at VWAP', met: pullbackHeldAboveVwap },
                    { label: 'Breakout pressure',     met: currentPrice != null && vwap != null && currentPrice > vwap },
                  ]}
                />
                <VwapChecklist
                  title="Put Setup"
                  score={putVwapScore}
                  conditions={[
                    { label: 'Price below VWAP',         met: priceAboveVwap === false },
                    { label: 'VWAP sloping down',         met: vwapSlopingDown },
                    { label: 'Pullback failed at VWAP',   met: pullbackFailedAtVwap },
                    { label: 'Breakdown pressure',        met: currentPrice != null && vwap != null && currentPrice < vwap },
                  ]}
                />
              </div>

              {/* Institutional guidance note */}
              <div className="vwap-note">
                {vwapSetupSignal === 'CALL_SETUP'
                  ? 'VWAP holding as support. Institutions accumulating. Enter CE on next breakout above recent high.'
                  : vwapSetupSignal === 'PUT_SETUP'
                  ? 'VWAP acting as resistance. Institutions distributing. Enter PE on next breakdown below recent low.'
                  : 'VWAP is the institutional reference. Wait for price to confirm direction relative to VWAP.'}
              </div>
            </div>
          ) : (
            <div className="vwap-loading">
              <span className="vwap-loading-icon">📊</span>
              <span>VWAP builds from today&apos;s bars ({todayBarCount}/3 min). Resets at each market open.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmaPanel;
